import type { PlannerTask, TaskPriority } from './types'

const DAY = 86_400_000

export const PRIORITY_WEIGHT: Record<TaskPriority, number> = {
  low: 1,
  normal: 2,
  high: 3,
  critical: 4,
}

export function startOfLocalDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate())
}

export function dateKey(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function localDateFromKey(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function startOfWorkWeek(value = new Date()): Date {
  const day = startOfLocalDay(value)
  const weekday = day.getDay() || 7
  day.setDate(day.getDate() - weekday + 1)
  return day
}

export function workWeekDays(value = new Date()): Date[] {
  const monday = startOfWorkWeek(value)
  return Array.from({ length: 5 }, (_, index) => {
    const day = new Date(monday)
    day.setDate(monday.getDate() + index)
    return day
  })
}

export function calendarDays(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  const gridStart = startOfWorkWeek(first)
  if (gridStart.getTime() > first.getTime()) gridStart.setDate(gridStart.getDate() - 7)

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart)
    day.setDate(gridStart.getDate() + index)
    return day
  })
}

export function sortTasks(tasks: PlannerTask[]): PlannerTask[] {
  return [...tasks].sort((left, right) => {
    if (left.completed !== right.completed) return left.completed ? 1 : -1
    const priority = PRIORITY_WEIGHT[right.priority] - PRIORITY_WEIGHT[left.priority]
    if (priority) return priority
    return new Date(left.dueAt).getTime() - new Date(right.dueAt).getTime()
  })
}

export function sortAgendaTasks(tasks: PlannerTask[]): PlannerTask[] {
  return [...tasks].sort((left, right) => {
    if (left.completed !== right.completed) return left.completed ? 1 : -1
    const due = new Date(left.dueAt).getTime() - new Date(right.dueAt).getTime()
    if (due) return due
    return PRIORITY_WEIGHT[right.priority] - PRIORITY_WEIGHT[left.priority]
  })
}

export function tasksForDate(tasks: PlannerTask[], day: Date): PlannerTask[] {
  const key = dateKey(day)
  return sortTasks(tasks.filter((task) => dateKey(task.dueAt) === key))
}

export function reminderAt(task: PlannerTask): number | null {
  if (task.reminderMinutes === null) return null
  return new Date(task.dueAt).getTime() - task.reminderMinutes * 60_000
}

export function isOverdue(task: PlannerTask, now = new Date()): boolean {
  return !task.completed && new Date(task.dueAt).getTime() < now.getTime()
}

export function daysBetween(left: Date, right: Date): number {
  return Math.round((startOfLocalDay(right).getTime() - startOfLocalDay(left).getTime()) / DAY)
}
